import {
  deleteProjectResponseSchema,
  keywordListResponseSchema,
  negativeKeywordSchema,
  okResponseSchema,
  projectDetailResponseSchema,
  projectLifecycleResponseSchema,
  projectListResponseSchema,
  projectResyncResponseSchema,
  runProjectNowResponseSchema,
  sourceGroupSchema,
  type CreateKeywordRequest,
  type CreateNegativeKeywordRequest,
  type CreateProjectRequest,
  type CreateSourceGroupRequest,
  type PaginationMeta,
  type ProjectDetailResponse,
  type ProjectLifecycleAction,
  type ProjectLifecycleResponse,
  type ProjectListItem,
  type ProjectResyncResponse,
  type ProjectStatus,
  type ProjectStatusChanged,
  type RunProjectNowResponse,
  type SearchCycleProgress,
  type UpdateProjectRequest,
  type UpdateSourceGroupRequest,
} from '@midiautolistening/contracts';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost, toApiError } from '@/lib/api';

/**
 * Projects: list, CRUD, keywords and the four lifecycle actions.
 *
 * A lifecycle action does not flip the status locally. The API answers with the
 * transition it recorded *and* a `commandId` for the command dispatched to the
 * agent; the definitive status arrives over the WebSocket as
 * `project.status_changed`. `lastLifecycleResult` keeps the returned command so
 * the UI can show "đã gửi lệnh, đang chờ agent xác nhận" instead of pretending the
 * work is finished.
 */

/** The two buttons that talk to the agent without changing the project's status. */
export type ManualProjectAction = 'run-now' | 'resync';

export interface ProjectListFilters {
  page: number;
  pageSize: number;
  search: string;
  status: ProjectStatus | '';
}

export const useProjectsStore = defineStore('projects', () => {
  const items = ref<ProjectListItem[]>([]);
  const meta = ref<PaginationMeta | null>(null);
  const loading = ref(false);
  const error = ref<ApiRequestError | null>(null);

  const filters = ref<ProjectListFilters>({ page: 1, pageSize: 25, search: '', status: '' });

  const detail = ref<ProjectDetailResponse | null>(null);
  const detailLoading = ref(false);
  const detailError = ref<ApiRequestError | null>(null);

  const submitting = ref(false);
  /** Project id -> action currently in flight, so only that row shows a spinner. */
  const lifecycleInFlight = ref<Record<string, ProjectLifecycleAction>>({});
  const lastLifecycleResult = ref<ProjectLifecycleResponse | null>(null);
  /** Project id -> manual action in flight, kept apart from the lifecycle four. */
  const manualInFlight = ref<Record<string, ManualProjectAction>>({});

  async function fetchList(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const response = await apiGet('/projects', projectListResponseSchema, {
        page: filters.value.page,
        pageSize: filters.value.pageSize,
        search: filters.value.search || undefined,
        status: filters.value.status || undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      });
      items.value = response.items;
      meta.value = response.meta;
    } catch (caught) {
      error.value = toApiError(caught);
      items.value = [];
      meta.value = null;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDetail(projectId: string): Promise<void> {
    detailLoading.value = true;
    detailError.value = null;
    try {
      detail.value = await apiGet(`/projects/${projectId}`, projectDetailResponseSchema);
    } catch (caught) {
      detailError.value = toApiError(caught);
      detail.value = null;
    } finally {
      detailLoading.value = false;
    }
  }

  function clearDetail(): void {
    detail.value = null;
    detailError.value = null;
  }

  async function createProject(payload: CreateProjectRequest): Promise<ProjectDetailResponse> {
    submitting.value = true;
    try {
      const response = await apiPost('/projects', projectDetailResponseSchema, payload);
      await fetchList();
      return response;
    } finally {
      submitting.value = false;
    }
  }

  async function updateProject(
    projectId: string,
    payload: UpdateProjectRequest,
  ): Promise<ProjectDetailResponse> {
    submitting.value = true;
    try {
      const response = await apiPatch(
        `/projects/${projectId}`,
        projectDetailResponseSchema,
        payload,
      );
      detail.value = response;
      patchListItem(projectId, response);
      return response;
    } finally {
      submitting.value = false;
    }
  }

  async function deleteProject(projectId: string): Promise<void> {
    submitting.value = true;
    try {
      await apiDelete(`/projects/${projectId}`, deleteProjectResponseSchema);
      items.value = items.value.filter((item) => item.id !== projectId);
      if (detail.value?.project.id === projectId) clearDetail();
    } finally {
      submitting.value = false;
    }
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  async function runLifecycle(
    projectId: string,
    action: ProjectLifecycleAction,
    body: Record<string, unknown> = {},
  ): Promise<ProjectLifecycleResponse> {
    lifecycleInFlight.value = { ...lifecycleInFlight.value, [projectId]: action };
    try {
      const response = await apiPost(
        `/projects/${projectId}/${action}`,
        projectLifecycleResponseSchema,
        body,
      );
      lastLifecycleResult.value = response;
      applyStatus(projectId, response.status, null);
      return response;
    } finally {
      const next = { ...lifecycleInFlight.value };
      delete next[projectId];
      lifecycleInFlight.value = next;
    }
  }

  // -------------------------------------------------------------------------
  // Manual agent actions
  // -------------------------------------------------------------------------

  /**
   * Ask for one cycle right now.
   *
   * Nothing is patched locally on success: the endpoint only answers once the
   * agent has the command, and the real progress arrives over the socket. A
   * refusal (cooldown, agent offline, project not RUNNING, agent said no) comes
   * back as an `ApiRequestError` and is rendered as an error, never as a toast
   * claiming the scan started.
   */
  async function runNow(projectId: string): Promise<RunProjectNowResponse> {
    manualInFlight.value = { ...manualInFlight.value, [projectId]: 'run-now' };
    try {
      return await apiPost(`/projects/${projectId}/run-now`, runProjectNowResponseSchema, {});
    } finally {
      clearManualInFlight(projectId);
    }
  }

  /** Re-push configuration and the published SelectorPack to this project's agent. */
  async function resync(projectId: string): Promise<ProjectResyncResponse> {
    manualInFlight.value = { ...manualInFlight.value, [projectId]: 'resync' };
    try {
      return await apiPost(`/projects/${projectId}/resync`, projectResyncResponseSchema, {});
    } finally {
      clearManualInFlight(projectId);
    }
  }

  function clearManualInFlight(projectId: string): void {
    const next = { ...manualInFlight.value };
    delete next[projectId];
    manualInFlight.value = next;
  }

  // -------------------------------------------------------------------------
  // Keywords
  // -------------------------------------------------------------------------

  async function addKeyword(projectId: string, payload: CreateKeywordRequest): Promise<void> {
    const response = await apiPost(
      `/projects/${projectId}/keywords`,
      keywordListResponseSchema,
      payload,
    );
    if (detail.value?.project.id === projectId) {
      detail.value = { ...detail.value, keywords: response.items };
    }
  }

  async function removeKeyword(projectId: string, keywordId: string): Promise<void> {
    await apiDelete(`/projects/${projectId}/keywords/${keywordId}`, okResponseSchema);
    if (detail.value?.project.id === projectId) {
      detail.value = {
        ...detail.value,
        keywords: detail.value.keywords.filter((keyword) => keyword.id !== keywordId),
      };
    }
  }

  async function addNegativeKeyword(
    projectId: string,
    payload: CreateNegativeKeywordRequest,
  ): Promise<void> {
    const created = await apiPost(
      `/projects/${projectId}/negative-keywords`,
      negativeKeywordSchema,
      payload,
    );
    if (detail.value?.project.id === projectId) {
      detail.value = {
        ...detail.value,
        negativeKeywords: [...detail.value.negativeKeywords, created],
      };
    }
  }

  async function removeNegativeKeyword(projectId: string, keywordId: string): Promise<void> {
    await apiDelete(`/projects/${projectId}/negative-keywords/${keywordId}`, okResponseSchema);
    if (detail.value?.project.id === projectId) {
      detail.value = {
        ...detail.value,
        negativeKeywords: detail.value.negativeKeywords.filter(
          (keyword) => keyword.id !== keywordId,
        ),
      };
    }
  }

  // -------------------------------------------------------------------------
  // Source groups
  // -------------------------------------------------------------------------

  async function addSourceGroup(
    projectId: string,
    payload: CreateSourceGroupRequest,
  ): Promise<void> {
    const created = await apiPost(
      `/projects/${projectId}/source-groups`,
      sourceGroupSchema,
      payload,
    );
    if (detail.value?.project.id === projectId) {
      detail.value = { ...detail.value, sourceGroups: [...detail.value.sourceGroups, created] };
    }
  }

  async function updateSourceGroup(
    projectId: string,
    sourceGroupId: string,
    payload: UpdateSourceGroupRequest,
  ): Promise<void> {
    const updated = await apiPatch(
      `/projects/${projectId}/source-groups/${sourceGroupId}`,
      sourceGroupSchema,
      payload,
    );
    if (detail.value?.project.id === projectId) {
      detail.value = {
        ...detail.value,
        sourceGroups: detail.value.sourceGroups.map((group) =>
          group.id === sourceGroupId ? updated : group,
        ),
      };
    }
  }

  async function removeSourceGroup(projectId: string, sourceGroupId: string): Promise<void> {
    await apiDelete(`/projects/${projectId}/source-groups/${sourceGroupId}`, okResponseSchema);
    if (detail.value?.project.id === projectId) {
      detail.value = {
        ...detail.value,
        sourceGroups: detail.value.sourceGroups.filter((group) => group.id !== sourceGroupId),
      };
    }
  }

  // -------------------------------------------------------------------------
  // Local patching (REST responses and realtime events share this path)
  // -------------------------------------------------------------------------

  function patchListItem(projectId: string, response: ProjectDetailResponse): void {
    items.value = items.value.map((item) =>
      item.id === projectId ? { ...item, ...response.project, counts: response.counts } : item,
    );
  }

  function applyStatus(
    projectId: string,
    status: ProjectStatus,
    statusMessage: string | null,
  ): void {
    items.value = items.value.map((item) =>
      item.id === projectId ? { ...item, status, statusMessage } : item,
    );

    if (detail.value?.project.id === projectId) {
      detail.value = {
        ...detail.value,
        project: { ...detail.value.project, status, statusMessage },
        runtime: { ...detail.value.runtime, status, statusMessage },
      };
    }
  }

  /** Realtime: `project.status_changed`. */
  function applyProjectStatusChanged(event: ProjectStatusChanged): void {
    applyStatus(event.projectId, event.status, event.statusMessage);

    if (detail.value?.project.id === event.projectId) {
      detail.value = {
        ...detail.value,
        runtime: {
          ...detail.value.runtime,
          allowedTransitions: event.allowedTransitions,
          since: event.occurredAt,
        },
      };
    }
  }

  /**
   * Realtime: `search.cycle_progress` drives the "Chu kỳ vừa rồi" line.
   *
   * The server emits it on cycle start, every batch ack and completion, so the
   * line flips to "đang chạy" the moment "Chạy ngay" (or the scheduler) starts
   * a cycle and counts up live — instead of showing the PREVIOUS cycle's
   * verdict until someone reloads. The event carries no failure reason, so on
   * FAILED the list is refetched for the row's `lastCycleError`.
   */
  function applyCycleProgress(event: SearchCycleProgress): void {
    items.value = items.value.map((item) =>
      item.id === event.projectId
        ? {
            ...item,
            lastCycleStatus: event.status,
            lastCyclePostsFound: event.postsFound,
            lastCycleError: null,
          }
        : item,
    );
    if (event.status === 'FAILED') void fetchList();
  }

  /** Realtime: `post.ingested` bumps the project's post counters in the list. */
  function incrementPostCount(projectId: string, isNew: boolean): void {
    if (!isNew) return;
    items.value = items.value.map((item) =>
      item.id === projectId
        ? {
            ...item,
            counts: {
              ...item.counts,
              postMatchCount: item.counts.postMatchCount + 1,
              unreviewedPostCount: item.counts.unreviewedPostCount + 1,
            },
          }
        : item,
    );
  }

  function reset(): void {
    items.value = [];
    meta.value = null;
    error.value = null;
    detail.value = null;
    detailError.value = null;
    lastLifecycleResult.value = null;
    lifecycleInFlight.value = {};
    manualInFlight.value = {};
    filters.value = { page: 1, pageSize: 25, search: '', status: '' };
  }

  return {
    items,
    meta,
    loading,
    error,
    filters,
    detail,
    detailLoading,
    detailError,
    submitting,
    lifecycleInFlight,
    lastLifecycleResult,
    manualInFlight,
    fetchList,
    fetchDetail,
    clearDetail,
    createProject,
    updateProject,
    deleteProject,
    runLifecycle,
    runNow,
    resync,
    addKeyword,
    removeKeyword,
    addNegativeKeyword,
    removeNegativeKeyword,
    addSourceGroup,
    updateSourceGroup,
    removeSourceGroup,
    applyProjectStatusChanged,
    applyCycleProgress,
    incrementPostCount,
    reset,
  };
});
